import { useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useParams,
} from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import { Toaster } from "sonner";
import { Button } from "./components/Button";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ErrorModal } from "./components/ErrorModal";
import { GameBoardRouter } from "./components/GameBoardRouter";
import { GlobalHeader } from "./components/GlobalHeader";
import { InstallPrompt } from "./components/InstallPrompt";
import { ScrollToTop } from "./components/landing/ScrollToTop";
import { Lobby } from "./components/Lobby";
import { MagicLink } from "./components/MagicLink";
import { Modal } from "./components/Modal";
import { SimpleTest } from "./pages/SimpleTest";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { RoomList } from "./components/RoomList";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { useFontLoader } from "./hooks/useFontLoader";
import { useGlobalKeyboardShortcuts } from "./hooks/useGlobalKeyboardShortcuts";
import { api } from "./lib/api";
import { withAuthRetry } from "./lib/authRetry";
import { logger } from "./lib/logger";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { useAuthStore } from "./store/authStore";
import { useGameStore } from "./store/gameStore";
import { useThemeStore } from "./store/themeStore";
// Lazy load infrequently accessed pages for code splitting
const AdminDashboard = lazy(() =>
  import("./components/AdminDashboard").then((m) => ({
    default: m.AdminDashboard,
  })),
);
const SettingsPage = lazy(() =>
  import("./components/SettingsPage").then((m) => ({
    default: m.SettingsPage,
  })),
);
const SpectatePage = lazy(() =>
  import("./pages/SpectatePage").then((m) => ({ default: m.SpectatePage })),
);
const SpectatorJoinForm = lazy(() =>
  import("./components/SpectatorJoinForm").then((m) => ({
    default: m.SpectatorJoinForm,
  })),
);
const TournamentPage = lazy(() =>
  import("./pages/TournamentPage").then((m) => ({ default: m.TournamentPage })),
);
const CardMechanicsTest = lazy(() =>
  import("./pages/CardMechanicsTest").then((m) => ({
    default: m.CardMechanicsTest,
  })),
);
const StatusComponentsTest = lazy(() =>
  import("./pages/StatusComponentsTest").then((m) => ({
    default: m.StatusComponentsTest,
  })),
);
const ContentDemo = lazy(() =>
  import("./pages/content-demo/ContentDemo").then((m) => ({
    default: m.ContentDemo,
  })),
);
const SoundDemo = lazy(() =>
  import("./pages/SoundDemo").then((m) => ({
    default: m.SoundDemo,
  })),
);
const CelebrationDemo = lazy(() =>
  import("./pages/CelebrationDemo").then((m) => ({
    default: m.CelebrationDemo,
  })),
);
const TransitionsDemo = lazy(() =>
  import("./pages/TransitionsDemo").then((m) => ({
    default: m.TransitionsDemo,
  })),
);
const TestIndex = lazy(() =>
  import("./pages/TestIndex").then((m) => ({
    default: m.TestIndex,
  })),
);
const PartyGameMockup = lazy(() =>
  import("./pages/mockups/PartyGameMockup").then((m) => ({
    default: m.PartyGameMockup,
  })),
);
const QuizGameMockup = lazy(() =>
  import("./pages/mockups/QuizGameMockup").then((m) => ({
    default: m.QuizGameMockup,
  })),
);
const BluffGameMockup = lazy(() =>
  import("./pages/mockups/BluffGameMockup").then((m) => ({
    default: m.BluffGameMockup,
  })),
);
const GameUXDemo = lazy(() =>
  import("./pages/GameUXDemo").then((m) => ({
    default: m.GameUXDemo,
  })),
);
const PolishDemo = lazy(() =>
  import("./pages/demos/PolishDemo").then((m) => ({
    default: m.default,
  })),
);

const AppView = {
  ROOM_LIST: 0,
  LOBBY: 1,
  GAME: 2,
} as const;
type AppView = (typeof AppView)[keyof typeof AppView];

/** Global signed out modal - shared by UserMenu and keyboard shortcuts */
function SignedOutModal() {
  const { showSignedOutModal, setShowSignedOutModal } = useAuthStore();

  return (
    <Modal
      isOpen={showSignedOutModal}
      onClose={() => setShowSignedOutModal(false)}
      title="Signed Out"
    >
      <div className="text-center py-4">
        <div className="text-6xl mb-4">👋</div>
        <p
          className="text-lg mb-6"
          style={{ color: "var(--color-text-primary)" }}
        >
          You have been successfully signed out.
        </p>
        <Button
          onClick={() => setShowSignedOutModal(false)}
          variant="primary"
          className="mx-auto"
        >
          OK
        </Button>
      </div>
    </Modal>
  );
}

function MainApp() {
  const { sessionId, playerId, gameState } = useGameStore();
  const location = useLocation();
  const urlParams = useParams();

  // Debug: Log when MainApp mounts/unmounts
  useEffect(() => {
    logger.debug("[MainApp] 🟢 MOUNTED", {
      sessionId,
      playerId,
      pathname: location.pathname,
    });
    return () => {
      logger.debug("[MainApp] 🔴 UNMOUNTING", {
        sessionId,
        playerId,
        pathname: location.pathname,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = only on mount/unmount

  // Derive view from URL and state
  const view = (() => {
    // URL-based routing takes precedence
    if (location.pathname.startsWith("/lobby/")) {
      // Trust the URL - if user is on /lobby/:id, show lobby
      // The Lobby component will handle fetching session data if needed
      return AppView.LOBBY;
    }
    if (location.pathname.startsWith("/game/")) {
      // Trust the URL - if user is on /game/:id, show game
      // The GameBoardRouter component will handle fetching session data if needed
      return AppView.GAME;
    }

    // Fallback to state-based routing (for backward compatibility)
    if (location.pathname === "/admin") return AppView.ROOM_LIST;
    if (!sessionId) return AppView.ROOM_LIST;
    if (gameState) {
      // Check both room_status and phase for lobby detection
      // Some games use room_status, others use phase
      // Consider it lobby phase ONLY if both room_status AND phase are 'lobby' (or unset)
      const extendedState = gameState as unknown as { phase?: string };
      const roomStatus = gameState.room_status;
      const phase = extendedState?.phase;

      // If phase is set and not 'lobby', game has started
      if (phase && phase !== "lobby") return AppView.GAME;
      // Otherwise fall back to room_status
      if (roomStatus && roomStatus !== "lobby") return AppView.GAME;
      // Both are 'lobby' or unset - show lobby
      return AppView.LOBBY;
    }
    return AppView.ROOM_LIST;
  })();

  // This callback is a no-op since view is derived from store state
  const handleRoomJoined = () => {
    // View will update automatically when store.sessionId and store.gameState change
  };

  // Check if we should show game views (have session)
  const hasSession = Boolean(sessionId && playerId);

  // Render views - WebSocket is managed by WebSocketProvider context
  return (
    <div className="min-h-screen">
      {view === AppView.ROOM_LIST && (
        <RoomList onRoomJoined={handleRoomJoined} />
      )}
      {view === AppView.LOBBY && hasSession && <Lobby />}
      {view === AppView.GAME && hasSession && <GameBoardRouter />}
    </div>
  );
}

function AdminRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          Loading admin...
        </div>
      }
    >
      <AdminDashboard onExit={() => (window.location.href = "/")} />
    </Suspense>
  );
}

function SettingsRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          Loading settings...
        </div>
      }
    >
      <SettingsPage />
    </Suspense>
  );
}

function SpectatorJoinRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading spectator...
        </div>
      }
    >
      <SpectatorJoinForm />
    </Suspense>
  );
}

function SpectateRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading spectator mode...
        </div>
      }
    >
      <SpectatePage />
    </Suspense>
  );
}

function TournamentRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          Loading tournaments...
        </div>
      }
    >
      <TournamentPage />
    </Suspense>
  );
}

function CardMechanicsTestRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading card test...
        </div>
      }
    >
      <CardMechanicsTest />
    </Suspense>
  );
}

function StatusComponentsTestRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading status components test...
        </div>
      }
    >
      <StatusComponentsTest />
    </Suspense>
  );
}

function ContentDemoRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading content demo...
        </div>
      }
    >
      <ContentDemo />
    </Suspense>
  );
}

function SoundDemoRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading sound demo...
        </div>
      }
    >
      <SoundDemo />
    </Suspense>
  );
}

function CelebrationDemoRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading celebration demo...
        </div>
      }
    >
      <CelebrationDemo />
    </Suspense>
  );
}

function TransitionsDemoRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading transitions demo...
        </div>
      }
    >
      <TransitionsDemo />
    </Suspense>
  );
}

function TestIndexRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading test hub...
        </div>
      }
    >
      <TestIndex />
    </Suspense>
  );
}

function PartyGameMockupRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading party game...
        </div>
      }
    >
      <PartyGameMockup />
    </Suspense>
  );
}

function QuizGameMockupRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading quiz game...
        </div>
      }
    >
      <QuizGameMockup />
    </Suspense>
  );
}

function BluffGameMockupRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading bluff game...
        </div>
      }
    >
      <BluffGameMockup />
    </Suspense>
  );
}

function GameUXDemoRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading game UX demo...
        </div>
      }
    >
      <GameUXDemo />
    </Suspense>
  );
}

function PolishDemoRoute() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Loading polish demo...
        </div>
      }
    >
      <PolishDemo />
    </Suspense>
  );
}

/**
 * Component that enables global keyboard shortcuts.
 * Must be rendered inside BrowserRouter to use navigation.
 */
function GlobalKeyboardShortcuts() {
  useGlobalKeyboardShortcuts();
  return null;
}

// Helper to decode JWT and get expiration time
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
}

function App() {
  const { initializeTheme } = useThemeStore();
  const { getAccessToken, clearAuth, isAuthenticated, setAuth } =
    useAuthStore();
  const fontsLoaded = useFontLoader();

  // Wire up auth token getter to API client
  useEffect(() => {
    api.setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Wire up 401 handler to attempt token refresh before clearing auth
  useEffect(() => {
    api.setUnauthorizedHandler(async () => {
      logger.warn("[App] 401 Unauthorized - attempting token refresh");
      const { refreshToken, user } = useAuthStore.getState();

      if (refreshToken && user) {
        try {
          const response = await withAuthRetry(
            () => api.refreshToken({ refresh_token: refreshToken }),
            "unauthorized_token_refresh",
          );
          logger.info("[App] Token refreshed successfully");
          // Update auth state with new tokens
          setAuth(user, response.access_token, response.refresh_token);
          return; // Don't clear auth - refresh succeeded
        } catch (refreshError) {
          logger.error("[App] Token refresh failed after retries", { error: refreshError });
        }
      }

      // Refresh failed or no refresh token - clear auth
      clearAuth();
      const { setSessionError } = useGameStore.getState();
      setSessionError(
        "Your session has expired. Please log in again to continue.",
      );
    });
  }, [clearAuth, setAuth]);

  // Monitor token expiration and refresh proactively before expiry
  useEffect(() => {
    if (!isAuthenticated()) return;

    const token = getAccessToken();
    if (!token) return;

    const expiryTime = getTokenExpiry(token);
    if (!expiryTime) return;

    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    // If already expired, try to refresh
    if (timeUntilExpiry <= 0) {
      logger.warn("[App] Token already expired - attempting refresh");
      const { refreshToken, user } = useAuthStore.getState();
      if (refreshToken && user) {
        withAuthRetry(
          () => api.refreshToken({ refresh_token: refreshToken }),
          "expired_token_refresh",
        )
          .then((response) => {
            logger.info("[App] Token refreshed after expiry");
            setAuth(user, response.access_token, response.refresh_token);
          })
          .catch(() => {
            logger.error("[App] Token refresh failed after expiry");
            clearAuth();
            const { setSessionError } = useGameStore.getState();
            setSessionError(
              "Your session has expired. Please log in again to continue.",
            );
          });
      } else {
        clearAuth();
        const { setSessionError } = useGameStore.getState();
        setSessionError(
          "Your session has expired. Please log in again to continue.",
        );
      }
      return;
    }

    // Refresh 5 minutes before expiry to prevent disruption during gameplay
    const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes
    const timeUntilRefresh = Math.max(0, timeUntilExpiry - REFRESH_BUFFER_MS);

    logger.debug("[App] Token expiry schedule", {
      expiresInMinutes: Math.round(timeUntilExpiry / 1000 / 60),
      refreshInMinutes: Math.round(timeUntilRefresh / 1000 / 60),
    });

    // Set timer to refresh token before it expires
    const refreshTimer = setTimeout(async () => {
      logger.info("[App] Proactively refreshing token before expiry");
      const { refreshToken, user } = useAuthStore.getState();
      if (refreshToken && user) {
        try {
          const response = await withAuthRetry(
            () => api.refreshToken({ refresh_token: refreshToken }),
            "proactive_token_refresh",
          );
          logger.info("[App] Token refreshed proactively");
          setAuth(user, response.access_token, response.refresh_token);
        } catch (error) {
          logger.error("[App] Proactive token refresh failed after retries", { error });
          // Don't immediately clear auth - let the expiry timer handle it
          // This gives the user time to continue playing until the token actually expires
        }
      }
    }, timeUntilRefresh);

    // Also set a fallback timer at actual expiry
    const expiryTimer = setTimeout(() => {
      // Check if token was refreshed (expiry would be different now)
      const currentToken = getAccessToken();
      if (currentToken) {
        const currentExpiry = getTokenExpiry(currentToken);
        if (currentExpiry && currentExpiry > Date.now()) {
          logger.debug(
            "[App] Token was already refreshed, skipping expiry handler",
          );
          return;
        }
      }

      logger.warn("[App] Token expired - attempting final refresh");
      const { refreshToken, user } = useAuthStore.getState();
      if (refreshToken && user) {
        withAuthRetry(
          () => api.refreshToken({ refresh_token: refreshToken }),
          "final_token_refresh",
        )
          .then((response) => {
            logger.info("[App] Token refreshed at expiry");
            setAuth(user, response.access_token, response.refresh_token);
          })
          .catch(() => {
            logger.error("[App] Final token refresh failed after retries");
            clearAuth();
            const { setSessionError } = useGameStore.getState();
            setSessionError(
              "Your session has expired. Please log in again to continue.",
            );
          });
      } else {
        clearAuth();
        const { setSessionError } = useGameStore.getState();
        setSessionError(
          "Your session has expired. Please log in again to continue.",
        );
      }
    }, timeUntilExpiry);

    return () => {
      clearTimeout(refreshTimer);
      clearTimeout(expiryTimer);
    };
  }, [getAccessToken, clearAuth, isAuthenticated, setAuth]);

  // Initialize theme from localStorage on app load
  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  // Refresh user profile on app load to get latest profile data (emoji, color)
  useEffect(() => {
    if (!isAuthenticated()) return;

    api
      .getProfile()
      .then((profile) => {
        // Update user profile in auth store with fresh data
        const { updateUserProfile } = useAuthStore.getState();
        updateUserProfile({
          profile_emoji: profile.profile_emoji,
          theme_primary_color: profile.theme_primary_color,
          theme_preference: profile.theme_preference,
        });
      })
      .catch(() => {
        // Silently ignore - user may be offline or token expired
      });
  }, [isAuthenticated]);

  // Wait for fonts to load to prevent FOUT
  if (!fontsLoaded) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "var(--color-background)",
        }}
      />
    );
  }

  return (
    <ErrorBoundary>
      <LazyMotion features={domAnimation}>
        <BrowserRouter>
          <GlobalKeyboardShortcuts />
          <WebSocketProvider>
            <Toaster position="top-right" richColors />
            <ErrorModal />
            <SignedOutModal />
            <GlobalHeader />
            <OfflineIndicator />
            <InstallPrompt />
            <ScrollToTop />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/admin" element={<AdminRoute />} />
              <Route path="/settings" element={<SettingsRoute />} />
              <Route path="/magic/:roomCode" element={<MagicLink />} />
              <Route path="/spectate/join" element={<SpectatorJoinRoute />} />
              <Route path="/spectate/:sessionId" element={<SpectateRoute />} />
              <Route path="/tournaments" element={<TournamentRoute />} />
              <Route path="/test/cards" element={<CardMechanicsTestRoute />} />
              <Route
                path="/test/status"
                element={<StatusComponentsTestRoute />}
              />
              <Route path="/test/content" element={<ContentDemoRoute />} />
              <Route path="/content-demo" element={<ContentDemoRoute />} />
              <Route path="/test/sounds" element={<SoundDemoRoute />} />
              <Route path="/test/celebrations" element={<CelebrationDemoRoute />} />
              <Route path="/test/transitions" element={<TransitionsDemoRoute />} />
              <Route path="/test" element={<TestIndexRoute />} />
              <Route path="/simple-test" element={<SimpleTest />} />
              <Route path="/test/mockup/party" element={<PartyGameMockupRoute />} />
              <Route path="/test/mockup/quiz" element={<QuizGameMockupRoute />} />
              <Route path="/test/mockup/bluff" element={<BluffGameMockupRoute />} />
              <Route path="/test/ux" element={<GameUXDemoRoute />} />
              <Route path="/test/polish" element={<PolishDemoRoute />} />
              {/* Single route for both lobby and game to prevent unmounting during transitions */}
              <Route path="/:view/:sessionId" element={<MainApp />} />
              <Route path="*" element={<MainApp />} />
            </Routes>
          </WebSocketProvider>
        </BrowserRouter>
      </LazyMotion>
    </ErrorBoundary>
  );
}

export default App;
